import React, { useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import './WbDailySummary.css';

function normalizeDailyStats(response) {
  const daily = Array.isArray(response?.daily) ? response.daily : [];
  return daily
    .filter((day) => day?.date)
    .map((day) => ({
      ...day,
      openCount: Number(day.openCount || 0),
      orderCount: Number(day.orderCount || 0),
      orderSum: Number(day.orderSum || 0),
    }))
    .sort((left, right) => String(right.date).localeCompare(String(left.date)));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatNumber(value) {
  return new Intl.NumberFormat('ru-RU').format(Number(value || 0));
}

function getDeltaClass(current, previous) {
  if (!previous) return '';
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return 'stable';
}

function getDeltaPercent(current, previous) {
  if (!previous) return 0;
  return ((current - previous) / previous) * 100;
}

export default function WbDailySummary({ accountId }) {
  const { request } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    loadDailySummary();
    // Refresh every 5 minutes
    const interval = setInterval(loadDailySummary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accountId]);

  const loadDailySummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request(`/api/v1/wb-stats/${accountId}`);
      setData({
        ...response,
        daily: normalizeDailyStats(response),
      });
    } catch (err) {
      setError(err?.message || 'Ошибка загрузки');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (!data && !loading && !error) {
    return null; // Not loaded yet
  }

  if (loading && !data) {
    return (
      <div className="wb-daily-summary">
        <div className="wb-daily-summary__loading">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wb-daily-summary">
        <div className="wb-daily-summary__error">⚠️ {error}</div>
      </div>
    );
  }

  const today = data?.daily?.[0];
  const yesterday = data?.daily?.[1];

  if (!today) {
    return null;
  }

  const todayRevenue = today.orderSum || 0;
  const yesterdayRevenue = yesterday?.orderSum || todayRevenue;
  const revenueDelta = getDeltaPercent(todayRevenue, yesterdayRevenue);

  const todayOrders = today.orderCount || 0;
  const yesterdayOrders = yesterday?.orderCount || todayOrders;
  const ordersDelta = getDeltaPercent(todayOrders, yesterdayOrders);

  const todayOpens = today.openCount || 0;
  const yesterdayOpens = yesterday?.openCount || todayOpens;
  const opensDelta = getDeltaPercent(todayOpens, yesterdayOpens);

  return (
    <div className="wb-daily-summary">
      <div className="wb-daily-summary__header">
        <h3 className="wb-daily-summary__title">📊 За последние 7 дней</h3>
        {loading && <div className="wb-daily-summary__refresh">обновляем...</div>}
      </div>

      <div className="wb-daily-summary__grid">
        <div className="wb-daily-summary__metric">
          <div className="wb-daily-summary__metric-label">Выручка (сегодня)</div>
          <div className="wb-daily-summary__metric-value">{formatCurrency(todayRevenue)}</div>
          {yesterday && (
            <div className={`wb-daily-summary__metric-delta wb-daily-summary__metric-delta--${getDeltaClass(todayRevenue, yesterdayRevenue)}`}>
              {revenueDelta > 0 ? '+' : ''}{revenueDelta.toFixed(1)}% vs вчера
            </div>
          )}
        </div>

        <div className="wb-daily-summary__metric">
          <div className="wb-daily-summary__metric-label">Заказы (сегодня)</div>
          <div className="wb-daily-summary__metric-value">{formatNumber(todayOrders)}</div>
          {yesterday && (
            <div className={`wb-daily-summary__metric-delta wb-daily-summary__metric-delta--${getDeltaClass(todayOrders, yesterdayOrders)}`}>
              {ordersDelta > 0 ? '+' : ''}{ordersDelta.toFixed(1)}% vs вчера
            </div>
          )}
        </div>

        <div className="wb-daily-summary__metric">
          <div className="wb-daily-summary__metric-label">Просмотры (сегодня)</div>
          <div className="wb-daily-summary__metric-value">{formatNumber(todayOpens)}</div>
          {yesterday && (
            <div className={`wb-daily-summary__metric-delta wb-daily-summary__metric-delta--${getDeltaClass(todayOpens, yesterdayOpens)}`}>
              {opensDelta > 0 ? '+' : ''}{opensDelta.toFixed(1)}% vs вчера
            </div>
          )}
        </div>

        <div className="wb-daily-summary__metric">
          <div className="wb-daily-summary__metric-label">Конверсия</div>
          <div className="wb-daily-summary__metric-value">
            {todayOpens > 0 ? ((todayOrders / todayOpens) * 100).toFixed(2) : '0'}%
          </div>
          <div className="wb-daily-summary__metric-hint">из просмотров в заказы</div>
        </div>
      </div>

      {/* Simple 7-day trend */}
      {data?.daily && data.daily.length > 0 && (
        <div className="wb-daily-summary__trend">
          <div className="wb-daily-summary__trend-label">Тренд выручки (7 дней)</div>
          <div className="wb-daily-summary__trend-bars">
            {[...data.daily].reverse().map((day) => {
              const maxRevenue = Math.max(...data.daily.map((d) => d.orderSum || 0));
              const height = maxRevenue > 0 ? (day.orderSum || 0) / maxRevenue : 0;
              const dateObj = new Date(`${String(day.date).slice(0, 10)}T00:00:00`);
              const dateLabel = dateObj.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

              return (
                <div key={day.date} className="wb-daily-summary__trend-bar-container">
                  <div
                    className="wb-daily-summary__trend-bar"
                    style={{ height: `${height * 100}%` }}
                    title={formatCurrency(day.orderSum || 0)}
                  />
                  <div className="wb-daily-summary__trend-label-small">{dateLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
